import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, OnInit, Renderer2, ViewChild, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Subscription } from 'rxjs';
import { ChatBotMapper } from '../Mappers/chat-bot.mapper';
import { ContextRequest, History, Incident, Message, System } from '../Models/chat-bot.model';
import { Config } from '../constants/constants';
import { SuccessFailureComponent } from '../custom-controls/modals/success-failure/success-failure.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MeteredgptChatBotService } from './meteredgpt-chat-bot.service';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MsalService } from '@azure/msal-angular';

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

interface FaqItem {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-meteredgpt-chat-bot',
  templateUrl: './meteredgpt-chat-bot.component.html',
  styleUrls: ['./meteredgpt-chat-bot.component.css']
})

export class MeteredgptChatBotComponent implements OnInit, AfterViewChecked {
  sseSubscription: Subscription;
  messages: Message[] = [];
  prompt: string;
  incident = new Incident();
  errorMessage: string;
  currentMessage: string;
  @ViewChild('pdfDashboard') pdfDashboard!: ElementRef;
  @ViewChild('chatScroll') private chatScrollContainer: ElementRef;
  maintainContext: boolean;
  currentContext: string;
  initialLoad = true;
  contextChangedFromAPI: boolean;
  modaldialogActions: Subscription;
  typeSpeed = 15;
  showLoader: boolean;
  system_prompt: string;
  chatHistory: History[] = [];
  disablePrompt: boolean;
  temperature = 0;
  ongoingBotResponse: string = '';
  showError: boolean = false;
  userprompt: string = '';
  regenerateResponse: boolean;
  showStop: boolean = false;
  downloadIcon = faDownload;
  faqs: FaqItem[] = [];
  concatenatedFaq: string = '';
  accessToken: string;
  isMobile: boolean;

  constructor(public chatService: MeteredgptChatBotService, private msalService: MsalService, private chatBotMapper: ChatBotMapper, private matDialog: MatDialog,
    private renderer: Renderer2, private elementRef: ElementRef, private cdr: ChangeDetectorRef, private sanitized: DomSanitizer, private http: HttpClient) {
      this.checkWindowSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkWindowSize();
  }

  checkWindowSize() {
    if (window.innerWidth < 768) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  onSliderChange(value: number) {
    this.temperature = value;
  }

  async ngOnInit() {
    await this.displayInitialMessage();
    this.initializeContextObj();
    this.chatService.conversation.subscribe(async (val: Message[]) => {
      if (val[0].author.toLocaleLowerCase() === 'bot') {
        if (val[0].type && val[0].type === 'error') {
          this.messages.forEach(message => {
            message.showLoader = false;
          })
          this.showError = true;
          this.showStop = false;
          this.cdr.detectChanges();
        } else {
          const existingMessageIndex = this.messages.findIndex(message => message.chatId === val[0].chatId);
          if (existingMessageIndex !== -1) {
            this.messages[existingMessageIndex].content.message = val[0].content.message;
            this.messages[existingMessageIndex].stopCursor = val[0].stopCursor;
          } else {
            this.messages = this.messages.concat(val);
          }

          const tempMessage = val[0].content.message;
          val[0].content.message = '';
          val[0].content.message = tempMessage;
          if (this.chatService.botResponse && this.chatService.botResponse.context) {
            this.maintainContext = this.chatService.botResponse.context.maintain ? this.chatService.botResponse.context.maintain : this.maintainContext;
          }
        }
      } else {
        this.messages = this.messages.concat(val);
      }
      this.scrollToBottom();
    });

    this.chatService.currentContext.subscribe((val: string) => {
      this.currentContext = val;
    });

    this.loadFaqs();
  }

  async displayInitialMessage() {
    const message = Config.BOT_MESSAGE;
    const initialReply: Message = this.chatBotMapper.MapMessages(false, message);
    initialReply.showLoader = false;
    initialReply.stopCursor = true;
    this.messages.push(initialReply);
    console.log("Initial Reply", initialReply)
  }

  loadFaqs(): void {
    this.http.get<{ faq: { question: string, answer: string }[] }>('assets/faq.json').subscribe(
      data => {
        this.faqs = data.faq;
        this.concatenatedFaq = this.concatenateFaqs(this.faqs);
        // console.log(this.concatenatedFaq);
      }
    );
  }

  concatenateFaqs(faqs: FaqItem[]): string {
    return faqs.map(faq => `Question: ${faq.question}\nAnswer: ${faq.answer}`).join('\n\n');
  }

  generatePDF(chatHistory: any[]) {
    const docDefinition: any = {
      content: [
        { text: 'Chat History', fontSize: 20, alignment: 'center', lineHeight: 2 },
      ]
    };
    chatHistory.forEach((chat) => {
      docDefinition.content.push(
        { text: chat.text, fontSize: 15, lineHeight: 1.2, alignment: chat.alignment, background: chat.background, margin: [0, 10] }
      )
    });
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    pdfDocGenerator.download('chat-history.pdf');
  }

  downloadChatHistoryPDF() {
    const chatHistory: any = [];
    this.messages.forEach(reply => {
      chatHistory.push({
        text: reply.authorDisplayName.toLocaleLowerCase() === Config.LEYA ? reply.authorDisplayName + ': ' + reply.content.message : reply.authorDisplayName + ': ' + reply.content.toString().replace('</br>', '\n'),
        alignment: reply.authorDisplayName.toLocaleLowerCase() === Config.LEYA ? 'left' : 'right',
        background: reply.authorDisplayName.toLocaleLowerCase() === Config.LEYA ? '#F0F1F5' : '#E6DCFF'
      });
    })
    this.generatePDF(chatHistory);
  }

  downloadMeteredDoc() {
    const link = document.createElement('a');
    link.href = 'assets/Metered-Stage0.pptx'; // Path to the file in the assets directory
    link.download = 'Metered - Stage 0.pptx'; // Desired file name
    link.click();
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    try {
      if (this.chatScrollContainer) {
        this.chatScrollContainer.nativeElement.scrollTop = this.chatScrollContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.log(err);
    }
  }

  initializeContextObj() {
    this.chatService.botContextRequest = new ContextRequest();

    this.chatService.contextObjList = [];
    const contextObjSystem = new System();
    contextObjSystem.type = Config.SYSTEM;
    contextObjSystem.client = Config.MOLSON;
    contextObjSystem.service_type = 'Databricks';
    this.chatService.contextObjList.push(contextObjSystem);

    this.chatService.botContextRequest.context_object = this.chatService.contextObjList;
  }

  regenerate() {
    this.regenerateResponse = true;
    this.submit();
  }
  async submit() {
    if (!this.showStop) {
      if (!this.regenerateResponse) {
        this.userprompt = this.prompt;
        this.disablePrompt = true;
        this.system_prompt = "You are an expert on the AIP+ Metered environment. Please use the following questions and answers when formulating a response back: "; //this.system_prompt ? this.system_prompt : ''
        this.system_prompt += this.concatenatedFaq;

        this.prompt = '';
        this.ongoingBotResponse = '';
        const userMessage = this.chatBotMapper.MapMessages(true, this.userprompt?.toString());
        userMessage.showLoader = true;
        this.showStop = true;
        this.chatService.conversation.next([userMessage]);
      }
      console.log("User Prompt", this.userprompt);
      console.log("System Prompt", this.system_prompt);

      const account = this.msalService.instance.getActiveAccount();

      if (!account) {
        console.error('No active account');
      }

      this.msalService.acquireTokenSilent({
        scopes: [environment.resource]
      }).subscribe((result) => {
        this.accessToken = result.accessToken;
        this.sseSubscription = this.chatService.connect(this.userprompt, this.system_prompt, this.chatHistory, this.accessToken).subscribe((event: MessageEvent) => {
          console.log('Received SSE message:', event.data);
          console.log('API Response:', this.sseSubscription);
          let streamResponse = JSON.parse(event.data);
          streamResponse.response = streamResponse.response.replace(/\n/g, '<br>');
          this.ongoingBotResponse += streamResponse.response;
          const botMessage = this.chatBotMapper.MapMessages(false, this.ongoingBotResponse, streamResponse.chat_id, streamResponse.stop);
          if (!!streamResponse) {
            this.messages.forEach(message => {
              message.showLoader = false;
            })
            this.showError = false;
            if (this.regenerateResponse && this.messages[this.messages.length - 1].author === 'bot' && !this.messages[this.messages.length - 1].stopCursor && !this.showError) {
              this.messages.pop();
            }
          }
          this.chatService.conversation.next([botMessage]);
          this.cdr.detectChanges();
          console.log(this.ongoingBotResponse);
          if (streamResponse.stop) {
            this.showStop = false;
            this.regenerateResponse = false;
            this.chatService.closeConnection();
            this.sseSubscription.unsubscribe();
            this.chatHistory.push({
              prompt: this.userprompt,
              answer: botMessage.content.message.toString()
            });
            this.cdr.detectChanges();
            this.chatService.conversation.next([botMessage]);
          }
        });
      });
    }
  }

  ngOnDestroy() {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
    }
  }

  resetData() {
    if (this.modaldialogActions !== undefined) {
      this.modaldialogActions.unsubscribe();
    }
    this.modaldialogActions = this.matDialog.open(SuccessFailureComponent, {
      disableClose: true,
      backdropClass: "backdropBackground",
      panelClass: "reset-container",
      data: {
        type: Config.ALERT,
        message: Config.RESET_MESSAGE
      }
    }).afterClosed().subscribe(async (modalData) => {
      if (modalData === 'Confirm') {
        this.initializeContextObj();
        this.incident = new Incident();
        this.chatService.botResponse = undefined;
        this.maintainContext = false;
        this.messages = [];
        this.temperature = 0;
        this.system_prompt = "";
        this.disablePrompt = false;
        await this.displayInitialMessage();
      }
      this.modaldialogActions.unsubscribe();
    });
  }

  stop() {
    this.messages.forEach(message => {
      message.showLoader = false;
      message.stopCursor = true;
    })
    this.ongoingBotResponse = '';
    this.showStop = false;
    this.chatHistory.push({
      prompt: this.userprompt,
      answer: this.messages[this.messages.length - 1].content.message.toString()
    });
    this.chatService.closeConnection();
    this.cdr.detectChanges();
  }
}
